import React from 'react';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { isJwtLoggedIn, loginLocationFor, onSessionEnded } from '../util/session';

/**
 * Keeps a signed-in section behind login, which returns the visitor to the page they wanted.
 *
 * Arriving without a session redirects before the section renders. A session that ends while a
 * page is open (noticed on returning to the tab, on navigating, or from a rejected request) leaves
 * through history instead, so a page's unsaved-work prompt can still keep the visitor there.
 */
export default function RequireSession({ section, children }: { section: string; children: React.ReactNode }) {
    const history = useHistory();
    const location = useLocation();
    const [signedInOnArrival] = React.useState(isJwtLoggedIn);
    // A visitor who chose to stay is not asked again for the same token until they navigate,
    // so background polling cannot repeat the prompt while they copy their work.
    const stayedWith = React.useRef<string | null>(null);

    const leaveIfEnded = React.useCallback(() => {
        const current = history.location;
        const inSection = current.pathname === section || current.pathname.startsWith(`${section}/`);
        const token = localStorage.getItem('jwt') ?? '';
        if (!inSection || isJwtLoggedIn() || stayedWith.current === token) return;
        history.replace(loginLocationFor(current, true));
        if (history.location === current) stayedWith.current = token;
    }, [history, section]);

    React.useEffect(() => {
        if (!signedInOnArrival) return;
        stayedWith.current = null;
        leaveIfEnded();
    }, [signedInOnArrival, leaveIfEnded, location]);

    React.useEffect(() => {
        if (!signedInOnArrival) return;
        const leaveIfVisible = () => {
            if (document.visibilityState === 'visible') leaveIfEnded();
        };
        document.addEventListener('visibilitychange', leaveIfVisible);
        window.addEventListener('focus', leaveIfEnded);
        const stopWatching = onSessionEnded(leaveIfEnded);
        return () => {
            document.removeEventListener('visibilitychange', leaveIfVisible);
            window.removeEventListener('focus', leaveIfEnded);
            stopWatching();
        };
    }, [signedInOnArrival, leaveIfEnded]);

    if (!signedInOnArrival) return <Redirect to={loginLocationFor(location, localStorage.getItem('jwt') !== null)} />;
    return <>{children}</>;
}
